import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';

import type { LocaleCode } from '../../../core/config/locales';
import { SUPPORTED_LOCALES } from '../../../core/config/locales';
import { LocaleService } from '../../../core/services/i18n/locale.service';
import { NfDialogComponent } from '../nf-dialog/nf-dialog.component';

/**
 * Dialog to pick the interface language, opened from the "Idioma" user-menu entry. Choosing a
 * language applies it at once; the dialog stays open so the change can be seen and undone, and
 * closes with Escape, the X button or the backdrop, returning focus to where it was opened.
 */
@Component({
  selector: 'nf-language-dialog',
  imports: [NfDialogComponent, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nf-dialog [title]="'layout.language.title' | transloco" size="sm" [showFooter]="false" (closed)="closed.emit()">
      <fieldset class="m-0 border-0 p-0">
        <legend class="mb-3 text-sm text-sk-text-secondary">{{ 'layout.language.description' | transloco }}</legend>
        @for (option of locales; track option.code) {
          <label
            class="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-sk-text hover:bg-sk-surface-hover">
            <input
              type="radio"
              name="nf-locale"
              class="h-4 w-4"
              [value]="option.code"
              [checked]="localeService.locale() === option.code"
              (change)="select(option.code)" />
            <span [attr.lang]="option.code">{{ option.label }}</span>
          </label>
        }
      </fieldset>
    </nf-dialog>
  `,
})
export class NfLanguageDialogComponent {
  protected readonly localeService = inject(LocaleService);
  protected readonly locales = SUPPORTED_LOCALES;

  /** Emitted when the dialog asks to be closed. */
  readonly closed = output<void>();

  protected select(code: LocaleCode): void {
    void this.localeService.setLocale(code);
  }
}
